import React, {useEffect, useState, useContext} from 'react';
import {Block} from '../block';
import {Tag, Row, Col, Form, Input, Switch} from 'antd';
import {isUndefined} from 'lodash';
import trim from 'trim';
import {MarkdownBlock} from '../markdown-block';
import {TextBlock} from '../text-block';
import {OpenApiContext} from '../../openapi-context';

function OperationTitle({name, operation, path, color}) {
    return (
        <Tag color={color} className="operationTitle">
            <span className="methodName" style={{color: color}}>
                {name}
            </span>{' '}
            {operation.path ? operation.path : path}
            {operation.operationId && <span className="operationId">({operation.operationId})</span>}
            {operation.tags ? (
                <span className="tags">
                    {operation.deprecated && <Tag color="red">deprecated</Tag>}
                    {operation.tags.map((tag) => (
                        <Tag key={tag} style={{margin: 5, maxWidth: '100%', whiteSpace: 'pre-wrap'}} color="geekblue">
                            {tag}
                        </Tag>
                    ))}
                </span>
            ) : (
                operation.deprecated && (
                    <span className="tags">
                        <Tag color="red">deprecated</Tag>
                    </span>
                )
            )}
        </Tag>
    );
}

function OperationHeaderEditor({name, path, deprecated, onChange, operationId, tags = []}) {
    const [form] = Form.useForm();

    const initialValues = {
        name,
        path,
        deprecated,
        operationId,
        tags: tags && tags.join(','),
    };

    const handleChanges = (changes) => {
        if (changes.tags) {
            changes.tags = changes.tags.split(',').map(trim);
        }

        onChange && onChange({type: 'merge-op-fields', fields: changes});
    };

    return (
        <Form form={form} initialValues={initialValues} onValuesChange={handleChanges}>
            <Row>
                <Col xs={18}>
                    <Form.Item label="Path" name="path">
                        <Input />
                    </Form.Item>
                </Col>
                <Col xs={5} offset={1}>
                    <Form.Item label="Deprecated?" name="deprecated" valuePropName="checked">
                        <Switch />
                    </Form.Item>
                </Col>
            </Row>
            <Row>
                <Col xs={12}>
                    <Form.Item label="Tags" name="tags">
                        <Input />
                    </Form.Item>
                </Col>
                <Col xs={10} offset={1}>
                    <Form.Item label="OperationId" name="operationId">
                        <Input />
                    </Form.Item>
                </Col>
            </Row>
        </Form>
    );
}

export function OperationHeaderBlock({operation, name, color, path, onChange, onSave, onCancel, delegatedEditMode, imageRootPath, mobile}) {
    const [editMode, setEditMode] = useState();
    const {dispatch} = useContext(OpenApiContext);

    useEffect(() => {
        if (!isUndefined(delegatedEditMode)) {
            setEditMode(delegatedEditMode);
        }
    }, [delegatedEditMode]);

    function handleSave() {
        dispatch({type: 'terminate'});
        setEditMode(false);
        return onSave && onSave();
    }

    function handleCancel() {
        dispatch({type: 'terminate'});
        setEditMode(false);
        return onCancel && onCancel();
    }

    function onEdit() {
        if (!delegatedEditMode) {
            setEditMode(true);
        }
    }

    const setSummary = (value) => onChange({type: 'merge-op-fields', fields: {summary: value}});
    const setDescription = (value) => onChange({type: 'merge-op-fields', fields: {description: value}});
    const setExternalDocs = (value) => onChange({type: 'merge-op-fields', fields: {externalDocs: value}});

    return (
        <Block delegatedEditMode={delegatedEditMode} onEdit={onEdit} onSave={handleSave} onCancel={handleCancel} mobile={mobile}>
            {editMode ? (
                <OperationHeaderEditor name={name} path={path} deprecated={operation.deprecated} tags={operation.tags} operationId={operation.operationId} onChange={onChange} />
            ) : (
                <OperationTitle name={name} operation={operation} path={path} color={color} />
            )}
            <TextBlock value={operation.summary} placeholder="Summary. A short summary of what the operation does." onValue={setSummary} delegatedEditMode={editMode} />
            <MarkdownBlock
                source={operation.description}
                size={300}
                placeholder={
                    <span>
                        <strong>Description</strong>. A verbose explanation of the operation behavior. CommonMark syntax MAY be used for rich text representation.
                    </span>
                }
                onValue={setDescription}
                delegatedEditMode={editMode}
                imageRootPath={imageRootPath}
            />
            <TextBlock value={operation.externalDocs} placeholder="ExternalDocs. Additional external documentation for this operation" onValue={setExternalDocs} delegatedEditMode={editMode} />
        </Block>
    );
}
